import { useEffect, useMemo, useState } from 'react';
import {
  computeAssetValue,
  createAsset,
  createAssetType,
  createCase,
  createCirculation,
  createDeliveryNote,
  createInvoice,
  createLocation,
  createPeripheral,
  deleteAsset,
  deleteAssetType,
  deleteCase,
  deleteCirculation,
  deleteDeliveryNote,
  deleteInvoice,
  deleteLocation,
  deletePeripheral,
  deliveryNoteDownloadUrl,
  fetchWarehouse,
  formatCurrency,
  invoiceDownloadUrl,
  updateAsset,
  updateAssetType,
  updateCase,
  updateLocation,
  updatePeripheral,
} from './api';
import type {
  Asset,
  AssetStatus,
  CaseRecord,
  CirculationRecord,
  DeliveryNote,
  Invoice,
  WarehouseData,
} from './types';
import './styles.css';

type TabKey =
  | 'dashboard'
  | 'types'
  | 'locations'
  | 'cases'
  | 'assets'
  | 'peripherals'
  | 'circulation'
  | 'documents';

const statusOptions: Array<{ value: AssetStatus; label: string }> = [
  { value: 'in_stock', label: 'Im Lager' },
  { value: 'outbound', label: 'Im Umlauf' },
  { value: 'maintenance', label: 'In Wartung' },
];

export default function App() {
  const [activeTab, setActiveTab] = useState<TabKey>('dashboard');
  const [data, setData] = useState<WarehouseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [typeForm, setTypeForm] = useState({ name: '', description: '' });
  const [locationForm, setLocationForm] = useState({ name: '', description: '' });
  const [caseForm, setCaseForm] = useState<{ label: string; locationId: string; notes: string }>({
    label: '',
    locationId: '',
    notes: '',
  });
  const [editingCaseId, setEditingCaseId] = useState<string | null>(null);
  const [assetForm, setAssetForm] = useState({
    name: '',
    typeId: '',
    locationId: '',
    caseId: '',
    serialNumber: '',
    status: 'in_stock' as AssetStatus,
    purchasePrice: '',
    currency: 'EUR',
  });
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [peripheralForm, setPeripheralForm] = useState({
    name: '',
    relatedAssetId: '',
    typeId: '',
    status: 'in_stock' as AssetStatus,
  });
  const [editingPeripheralId, setEditingPeripheralId] = useState<string | null>(null);
  const [circulationForm, setCirculationForm] = useState({
    assetId: '',
    action: 'dispatch' as CirculationRecord['action'],
    performedAt: new Date().toISOString().slice(0, 10),
    performedBy: '',
    notes: '',
    price: '',
    currency: 'EUR',
  });
  const [invoiceForm, setInvoiceForm] = useState(() => ({
    number: '',
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    customerName: '',
    customerAddress: '',
    currency: 'EUR',
    lineItems: [
      { assetId: '', description: '', quantity: 1, unitPrice: 0 },
    ],
  }));
  const [deliveryForm, setDeliveryForm] = useState(() => ({
    number: '',
    issueDate: new Date().toISOString().slice(0, 10),
    recipientName: '',
    recipientAddress: '',
    items: [{ assetId: '', description: '', quantity: 1 }],
  }));

  useEffect(() => {
    void loadData();
  }, []);

  const totals = useMemo(() => {
    if (!data) {
      return { totalAssets: 0, totalValue: 0, outbound: 0, maintenance: 0 };
    }
    const totalAssets = data.assets.length;
    const totalValue = computeAssetValue(data.assets);
    const outbound = data.assets.filter((asset) => asset.status === 'outbound').length;
    const maintenance = data.assets.filter((asset) => asset.status === 'maintenance').length;
    return { totalAssets, totalValue, outbound, maintenance };
  }, [data]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchWarehouse();
      setData(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function withReload<T>(callback: () => Promise<T>, successMessage?: string) {
    try {
      setMessage(null);
      setError(null);
      await callback();
      await loadData();
      if (successMessage) {
        setMessage(successMessage);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function renderDashboard() {
    if (!data) return null;
    return (
      <div className="card-grid">
        <div className="card">
          <h3>Gesamtgeräte</h3>
          <p className="card-value">{totals.totalAssets}</p>
        </div>
        <div className="card">
          <h3>Gesamtwert</h3>
          <p className="card-value">{formatCurrency(totals.totalValue)}</p>
        </div>
        <div className="card">
          <h3>Im Umlauf</h3>
          <p className="card-value">{totals.outbound}</p>
        </div>
        <div className="card">
          <h3>In Wartung</h3>
          <p className="card-value">{totals.maintenance}</p>
        </div>
        <div className="card">
          <h3>Letzte Aktualisierung</h3>
          <p className="card-value">{new Date(data.updatedAt).toLocaleString('de-DE')}</p>
        </div>
      </div>
    );
  }

  async function handleTypeSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await withReload(
      () =>
        typeForm.name.trim()
          ? createAssetType({ name: typeForm.name, description: typeForm.description })
          : Promise.reject(new Error('Name ist erforderlich.')),
      'Gerätetyp gespeichert.'
    );
    setTypeForm({ name: '', description: '' });
  }

  async function handleLocationSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await withReload(
      () =>
        locationForm.name.trim()
          ? createLocation({ name: locationForm.name, description: locationForm.description })
          : Promise.reject(new Error('Name ist erforderlich.')),
      'Lagerort gespeichert.'
    );
    setLocationForm({ name: '', description: '' });
  }

  async function handleCaseSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!caseForm.label.trim()) {
      setError('Case-Bezeichnung ist erforderlich.');
      return;
    }
    if (editingCaseId) {
      await withReload(
        () => updateCase(editingCaseId, caseForm),
        'Case aktualisiert.'
      );
    } else {
      await withReload(() => createCase(caseForm), 'Case gespeichert.');
    }
    setCaseForm({ label: '', locationId: '', notes: '' });
    setEditingCaseId(null);
  }

  async function handleAssetSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!assetForm.name.trim() || !assetForm.typeId) {
      setError('Name und Gerätetyp sind erforderlich.');
      return;
    }
    const payload: Omit<Asset, 'id'> = {
      name: assetForm.name,
      typeId: assetForm.typeId,
      locationId: assetForm.locationId || undefined,
      caseId: assetForm.caseId || undefined,
      serialNumber: assetForm.serialNumber || undefined,
      status: assetForm.status,
      purchasePrice: assetForm.purchasePrice ? Number(assetForm.purchasePrice) : undefined,
      currency: assetForm.purchasePrice ? assetForm.currency : undefined,
    };
    if (editingAssetId) {
      await withReload(() => updateAsset(editingAssetId, payload), 'Gerät aktualisiert.');
    } else {
      await withReload(() => createAsset(payload), 'Gerät gespeichert.');
    }
    setAssetForm({
      name: '',
      typeId: '',
      locationId: '',
      caseId: '',
      serialNumber: '',
      status: 'in_stock',
      purchasePrice: '',
      currency: 'EUR',
    });
    setEditingAssetId(null);
  }

  async function handlePeripheralSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!peripheralForm.name.trim()) {
      setError('Name ist erforderlich.');
      return;
    }
    const payload = {
      name: peripheralForm.name,
      relatedAssetId: peripheralForm.relatedAssetId || undefined,
      typeId: peripheralForm.typeId || undefined,
      status: peripheralForm.status,
    };
    if (editingPeripheralId) {
      await withReload(
        () => updatePeripheral(editingPeripheralId, payload),
        'Peripherie aktualisiert.'
      );
    } else {
      await withReload(() => createPeripheral(payload), 'Peripheriegerät gespeichert.');
    }
    setPeripheralForm({ name: '', relatedAssetId: '', typeId: '', status: 'in_stock' });
    setEditingPeripheralId(null);
  }

  async function handleCirculationSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!circulationForm.assetId) {
      setError('Es muss ein Gerät ausgewählt werden.');
      return;
    }
    const payload: Omit<CirculationRecord, 'id'> = {
      assetId: circulationForm.assetId,
      action: circulationForm.action,
      performedAt: circulationForm.performedAt,
      performedBy: circulationForm.performedBy || undefined,
      notes: circulationForm.notes || undefined,
      price: circulationForm.price ? Number(circulationForm.price) : undefined,
      currency: circulationForm.price ? circulationForm.currency : undefined,
    };
    await withReload(() => createCirculation(payload), 'Bewegung protokolliert.');
    setCirculationForm({
      assetId: '',
      action: 'dispatch',
      performedAt: new Date().toISOString().slice(0, 10),
      performedBy: '',
      notes: '',
      price: '',
      currency: 'EUR',
    });
  }

  function updateInvoiceItem(index: number, field: string, value: string | number) {
    setInvoiceForm((prev) => {
      const nextItems = prev.lineItems.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      );
      return { ...prev, lineItems: nextItems };
    });
  }

  async function handleInvoiceSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!invoiceForm.number.trim() || !invoiceForm.customerName.trim()) {
      setError('Nummer und Kunde sind erforderlich.');
      return;
    }
    const payload = {
      number: invoiceForm.number,
      issueDate: invoiceForm.issueDate,
      dueDate: invoiceForm.dueDate || undefined,
      customerName: invoiceForm.customerName,
      customerAddress: invoiceForm.customerAddress || undefined,
      currency: invoiceForm.currency,
      lineItems: invoiceForm.lineItems.map((item) => ({
        assetId: item.assetId || undefined,
        description: item.description || 'Position',
        quantity: Number(item.quantity) || 1,
        unitPrice: Number(item.unitPrice) || 0,
        currency: invoiceForm.currency,
      })),
    };
    await withReload(() => createInvoice(payload), 'Rechnung erstellt.');
    setInvoiceForm({
      number: '',
      issueDate: new Date().toISOString().slice(0, 10),
      dueDate: '',
      customerName: '',
      customerAddress: '',
      currency: 'EUR',
      lineItems: [{ assetId: '', description: '', quantity: 1, unitPrice: 0 }],
    });
  }

  function updateDeliveryItem(index: number, field: string, value: string | number) {
    setDeliveryForm((prev) => {
      const nextItems = prev.items.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      );
      return { ...prev, items: nextItems };
    });
  }

  async function handleDeliverySubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!deliveryForm.number.trim() || !deliveryForm.recipientName.trim()) {
      setError('Nummer und Empfänger sind erforderlich.');
      return;
    }
    const payload = {
      number: deliveryForm.number,
      issueDate: deliveryForm.issueDate,
      recipientName: deliveryForm.recipientName,
      recipientAddress: deliveryForm.recipientAddress || undefined,
      items: deliveryForm.items.map((item) => ({
        assetId: item.assetId || undefined,
        description: item.description || 'Position',
        quantity: Number(item.quantity) || 1,
      })),
    };
    await withReload(() => createDeliveryNote(payload), 'Lieferschein erstellt.');
    setDeliveryForm({
      number: '',
      issueDate: new Date().toISOString().slice(0, 10),
      recipientName: '',
      recipientAddress: '',
      items: [{ assetId: '', description: '', quantity: 1 }],
    });
  }

  function renderTypes() {
    if (!data) return null;
    return (
      <div>
        <form className="form" onSubmit={handleTypeSubmit}>
          <h3>Neuer Gerätetyp</h3>
          <label>
            Name
            <input
              value={typeForm.name}
              onChange={(event) => setTypeForm({ ...typeForm, name: event.target.value })}
              required
            />
          </label>
          <label>
            Beschreibung
            <input
              value={typeForm.description}
              onChange={(event) =>
                setTypeForm({ ...typeForm, description: event.target.value })
              }
            />
          </label>
          <button type="submit" className="primary">
            Speichern
          </button>
        </form>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Beschreibung</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {data.assetTypes.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.name}</td>
                <td>{entry.description ?? '—'}</td>
                <td className="table-actions">
                  <button
                    type="button"
                    onClick={() =>
                      void withReload(
                        () =>
                          updateAssetType(entry.id, {
                            name: prompt('Neuer Name', entry.name) ?? entry.name,
                            description:
                              prompt('Neue Beschreibung', entry.description ?? '') ??
                              entry.description ??
                              '',
                          })
                      )
                    }
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() =>
                      void withReload(() => deleteAssetType(entry.id), 'Typ gelöscht.')
                    }
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderLocations() {
    if (!data) return null;
    return (
      <div>
        <form className="form" onSubmit={handleLocationSubmit}>
          <h3>Neuer Lagerort</h3>
          <label>
            Name
            <input
              value={locationForm.name}
              onChange={(event) =>
                setLocationForm({ ...locationForm, name: event.target.value })
              }
              required
            />
          </label>
          <label>
            Beschreibung
            <input
              value={locationForm.description}
              onChange={(event) =>
                setLocationForm({ ...locationForm, description: event.target.value })
              }
            />
          </label>
          <button type="submit" className="primary">
            Speichern
          </button>
        </form>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Beschreibung</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {data.locations.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.name}</td>
                <td>{entry.description ?? '—'}</td>
                <td className="table-actions">
                  <button
                    type="button"
                    onClick={() =>
                      void withReload(
                        () =>
                          updateLocation(entry.id, {
                            name: prompt('Neuer Name', entry.name) ?? entry.name,
                            description:
                              prompt('Neue Beschreibung', entry.description ?? '') ??
                              entry.description ??
                              '',
                          })
                      )
                    }
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() =>
                      void withReload(() => deleteLocation(entry.id), 'Lagerort gelöscht.')
                    }
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderCases() {
    if (!data) return null;
    return (
      <div>
        <form className="form" onSubmit={handleCaseSubmit}>
          <h3>{editingCaseId ? 'Case bearbeiten' : 'Neuer Case'}</h3>
          <label>
            Bezeichnung
            <input
              value={caseForm.label}
              onChange={(event) =>
                setCaseForm({ ...caseForm, label: event.target.value })
              }
              required
            />
          </label>
          <label>
            Lagerort
            <select
              value={caseForm.locationId}
              onChange={(event) =>
                setCaseForm({ ...caseForm, locationId: event.target.value })
              }
            >
              <option value="">—</option>
              {data.locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Notizen
            <textarea
              value={caseForm.notes}
              onChange={(event) =>
                setCaseForm({ ...caseForm, notes: event.target.value })
              }
            />
          </label>
          <div className="form-actions">
            <button type="submit" className="primary">
              {editingCaseId ? 'Aktualisieren' : 'Speichern'}
            </button>
            {editingCaseId && (
              <button
                type="button"
                onClick={() => {
                  setCaseForm({ label: '', locationId: '', notes: '' });
                  setEditingCaseId(null);
                }}
              >
                Abbrechen
              </button>
            )}
          </div>
        </form>
        <table>
          <thead>
            <tr>
              <th>Case</th>
              <th>Lagerort</th>
              <th>Notizen</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {data.cases.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.label}</td>
                <td>{data.locations.find((loc) => loc.id === entry.locationId)?.name ?? '—'}</td>
                <td>{entry.notes ?? '—'}</td>
                <td className="table-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingCaseId(entry.id);
                      setCaseForm({
                        label: entry.label,
                        locationId: entry.locationId ?? '',
                        notes: entry.notes ?? '',
                      });
                    }}
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() =>
                      void withReload(() => deleteCase(entry.id), 'Case gelöscht.')
                    }
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderAssets() {
    if (!data) return null;
    return (
      <div>
        <form className="form" onSubmit={handleAssetSubmit}>
          <h3>{editingAssetId ? 'Gerät bearbeiten' : 'Neues Gerät'}</h3>
          <label>
            Name
            <input
              value={assetForm.name}
              onChange={(event) =>
                setAssetForm({ ...assetForm, name: event.target.value })
              }
              required
            />
          </label>
          <label>
            Typ
            <select
              value={assetForm.typeId}
              onChange={(event) =>
                setAssetForm({ ...assetForm, typeId: event.target.value })
              }
              required
            >
              <option value="">Bitte wählen</option>
              {data.assetTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Lagerort
            <select
              value={assetForm.locationId}
              onChange={(event) =>
                setAssetForm({ ...assetForm, locationId: event.target.value })
              }
            >
              <option value="">—</option>
              {data.locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Case
            <select
              value={assetForm.caseId}
              onChange={(event) =>
                setAssetForm({ ...assetForm, caseId: event.target.value })
              }
            >
              <option value="">—</option>
              {data.cases.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Seriennummer
            <input
              value={assetForm.serialNumber}
              onChange={(event) =>
                setAssetForm({ ...assetForm, serialNumber: event.target.value })
              }
            />
          </label>
          <label>
            Status
            <select
              value={assetForm.status}
              onChange={(event) =>
                setAssetForm({ ...assetForm, status: event.target.value as AssetStatus })
              }
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Anschaffungspreis
            <input
              type="number"
              min="0"
              step="0.01"
              value={assetForm.purchasePrice}
              onChange={(event) =>
                setAssetForm({ ...assetForm, purchasePrice: event.target.value })
              }
            />
          </label>
          <label>
            Währung
            <input
              value={assetForm.currency}
              onChange={(event) =>
                setAssetForm({ ...assetForm, currency: event.target.value.toUpperCase() })
              }
              maxLength={3}
            />
          </label>
          <div className="form-actions">
            <button type="submit" className="primary">
              {editingAssetId ? 'Aktualisieren' : 'Speichern'}
            </button>
            {editingAssetId && (
              <button
                type="button"
                onClick={() => {
                  setAssetForm({
                    name: '',
                    typeId: '',
                    locationId: '',
                    caseId: '',
                    serialNumber: '',
                    status: 'in_stock',
                    purchasePrice: '',
                    currency: 'EUR',
                  });
                  setEditingAssetId(null);
                }}
              >
                Abbrechen
              </button>
            )}
          </div>
        </form>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Typ</th>
              <th>Status</th>
              <th>Lagerort</th>
              <th>Case</th>
              <th>Preis</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {data.assets.map((asset) => (
              <tr key={asset.id}>
                <td>{asset.name}</td>
                <td>{data.assetTypes.find((type) => type.id === asset.typeId)?.name ?? '—'}</td>
                <td>{statusOptions.find((opt) => opt.value === asset.status)?.label}</td>
                <td>{data.locations.find((loc) => loc.id === asset.locationId)?.name ?? '—'}</td>
                <td>{data.cases.find((entry) => entry.id === asset.caseId)?.label ?? '—'}</td>
                <td>
                  {asset.purchasePrice
                    ? formatCurrency(asset.purchasePrice, asset.currency ?? 'EUR')
                    : '—'}
                </td>
                <td className="table-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingAssetId(asset.id);
                      setAssetForm({
                        name: asset.name,
                        typeId: asset.typeId,
                        locationId: asset.locationId ?? '',
                        caseId: asset.caseId ?? '',
                        serialNumber: asset.serialNumber ?? '',
                        status: asset.status,
                        purchasePrice: asset.purchasePrice?.toString() ?? '',
                        currency: asset.currency ?? 'EUR',
                      });
                    }}
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() =>
                      void withReload(() => deleteAsset(asset.id), 'Gerät gelöscht.')
                    }
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderPeripherals() {
    if (!data) return null;
    return (
      <div>
        <form className="form" onSubmit={handlePeripheralSubmit}>
          <h3>{editingPeripheralId ? 'Peripherie bearbeiten' : 'Neue Peripherie'}</h3>
          <label>
            Name
            <input
              value={peripheralForm.name}
              onChange={(event) =>
                setPeripheralForm({ ...peripheralForm, name: event.target.value })
              }
              required
            />
          </label>
          <label>
            Zugehöriges Gerät
            <select
              value={peripheralForm.relatedAssetId}
              onChange={(event) =>
                setPeripheralForm({ ...peripheralForm, relatedAssetId: event.target.value })
              }
            >
              <option value="">—</option>
              {data.assets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Typ
            <select
              value={peripheralForm.typeId}
              onChange={(event) =>
                setPeripheralForm({ ...peripheralForm, typeId: event.target.value })
              }
            >
              <option value="">—</option>
              {data.assetTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={peripheralForm.status}
              onChange={(event) =>
                setPeripheralForm({ ...peripheralForm, status: event.target.value as AssetStatus })
              }
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <div className="form-actions">
            <button type="submit" className="primary">
              {editingPeripheralId ? 'Aktualisieren' : 'Speichern'}
            </button>
            {editingPeripheralId && (
              <button
                type="button"
                onClick={() => {
                  setPeripheralForm({ name: '', relatedAssetId: '', typeId: '', status: 'in_stock' });
                  setEditingPeripheralId(null);
                }}
              >
                Abbrechen
              </button>
            )}
          </div>
        </form>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Gerät</th>
              <th>Typ</th>
              <th>Status</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {data.peripherals.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.name}</td>
                <td>{data.assets.find((asset) => asset.id === entry.relatedAssetId)?.name ?? '—'}</td>
                <td>{data.assetTypes.find((type) => type.id === entry.typeId)?.name ?? '—'}</td>
                <td>{statusOptions.find((opt) => opt.value === entry.status)?.label}</td>
                <td className="table-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingPeripheralId(entry.id);
                      setPeripheralForm({
                        name: entry.name,
                        relatedAssetId: entry.relatedAssetId ?? '',
                        typeId: entry.typeId ?? '',
                        status: entry.status,
                      });
                    }}
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() =>
                      void withReload(
                        () => deletePeripheral(entry.id),
                        'Peripherie gelöscht.'
                      )
                    }
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderCirculation() {
    if (!data) return null;
    return (
      <div>
        <form className="form" onSubmit={handleCirculationSubmit}>
          <h3>Bewegung erfassen</h3>
          <label>
            Gerät
            <select
              value={circulationForm.assetId}
              onChange={(event) =>
                setCirculationForm({ ...circulationForm, assetId: event.target.value })
              }
              required
            >
              <option value="">Bitte wählen</option>
              {data.assets.map((asset) => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Aktion
            <select
              value={circulationForm.action}
              onChange={(event) =>
                setCirculationForm({ ...circulationForm, action: event.target.value as CirculationRecord['action'] })
              }
            >
              <option value="dispatch">Ausgabe</option>
              <option value="return">Rücklauf</option>
              <option value="maintenance">Wartung</option>
            </select>
          </label>
          <label>
            Datum
            <input
              type="date"
              value={circulationForm.performedAt}
              onChange={(event) =>
                setCirculationForm({ ...circulationForm, performedAt: event.target.value })
              }
            />
          </label>
          <label>
            Verantwortlich
            <input
              value={circulationForm.performedBy}
              onChange={(event) =>
                setCirculationForm({ ...circulationForm, performedBy: event.target.value })
              }
            />
          </label>
          <label>
            Notizen
            <textarea
              value={circulationForm.notes}
              onChange={(event) =>
                setCirculationForm({ ...circulationForm, notes: event.target.value })
              }
            />
          </label>
          <label>
            Preis
            <input
              type="number"
              min="0"
              step="0.01"
              value={circulationForm.price}
              onChange={(event) =>
                setCirculationForm({ ...circulationForm, price: event.target.value })
              }
            />
          </label>
          <label>
            Währung
            <input
              value={circulationForm.currency}
              onChange={(event) =>
                setCirculationForm({
                  ...circulationForm,
                  currency: event.target.value.toUpperCase(),
                })
              }
              maxLength={3}
            />
          </label>
          <button type="submit" className="primary">
            Speichern
          </button>
        </form>
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Gerät</th>
              <th>Aktion</th>
              <th>Verantwortlich</th>
              <th>Preis</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {data.circulation
              .slice()
              .sort((a, b) => b.performedAt.localeCompare(a.performedAt))
              .map((entry) => (
                <tr key={entry.id}>
                  <td>{new Date(entry.performedAt).toLocaleDateString('de-DE')}</td>
                  <td>{data.assets.find((asset) => asset.id === entry.assetId)?.name ?? '—'}</td>
                  <td>{
                    entry.action === 'dispatch'
                      ? 'Ausgabe'
                      : entry.action === 'return'
                      ? 'Rücklauf'
                      : 'Wartung'
                  }</td>
                  <td>{entry.performedBy ?? '—'}</td>
                  <td>
                    {entry.price
                      ? formatCurrency(entry.price, entry.currency ?? 'EUR')
                      : '—'}
                  </td>
                  <td className="table-actions">
                    <button
                      type="button"
                      className="danger"
                      onClick={() =>
                        void withReload(
                          () => deleteCirculation(entry.id),
                          'Bewegung gelöscht.'
                        )
                      }
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderDocuments() {
    if (!data) return null;
    return (
      <div className="documents">
        <section>
          <form className="form" onSubmit={handleInvoiceSubmit}>
            <h3>Rechnung erstellen</h3>
            <div className="grid">
              <label>
                Nummer
                <input
                  value={invoiceForm.number}
                  onChange={(event) =>
                    setInvoiceForm({ ...invoiceForm, number: event.target.value })
                  }
                  required
                />
              </label>
              <label>
                Rechnungsdatum
                <input
                  type="date"
                  value={invoiceForm.issueDate}
                  onChange={(event) =>
                    setInvoiceForm({ ...invoiceForm, issueDate: event.target.value })
                  }
                  required
                />
              </label>
              <label>
                Fällig am
                <input
                  type="date"
                  value={invoiceForm.dueDate}
                  onChange={(event) =>
                    setInvoiceForm({ ...invoiceForm, dueDate: event.target.value })
                  }
                />
              </label>
              <label>
                Währung
                <input
                  value={invoiceForm.currency}
                  onChange={(event) =>
                    setInvoiceForm({
                      ...invoiceForm,
                      currency: event.target.value.toUpperCase(),
                    })
                  }
                  maxLength={3}
                />
              </label>
            </div>
            <label>
              Kunde
              <input
                value={invoiceForm.customerName}
                onChange={(event) =>
                  setInvoiceForm({ ...invoiceForm, customerName: event.target.value })
                }
                required
              />
            </label>
            <label>
              Adresse
              <textarea
                value={invoiceForm.customerAddress}
                onChange={(event) =>
                  setInvoiceForm({
                    ...invoiceForm,
                    customerAddress: event.target.value,
                  })
                }
              />
            </label>
            <div className="line-items">
              <h4>Positionen</h4>
              {invoiceForm.lineItems.map((item, index) => (
                <div className="line-item" key={index}>
                  <select
                    value={item.assetId}
                    onChange={(event) =>
                      updateInvoiceItem(index, 'assetId', event.target.value)
                    }
                  >
                    <option value="">—</option>
                    {data.assets.map((asset) => (
                      <option key={asset.id} value={asset.id}>
                        {asset.name}
                      </option>
                    ))}
                  </select>
                  <input
                    placeholder="Beschreibung"
                    value={item.description}
                    onChange={(event) =>
                      updateInvoiceItem(index, 'description', event.target.value)
                    }
                  />
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(event) =>
                      updateInvoiceItem(index, 'quantity', Number(event.target.value))
                    }
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unitPrice}
                    onChange={(event) =>
                      updateInvoiceItem(index, 'unitPrice', Number(event.target.value))
                    }
                  />
                  <button
                    type="button"
                    className="danger"
                    onClick={() =>
                      setInvoiceForm((prev) => ({
                        ...prev,
                        lineItems: prev.lineItems.filter((_, idx) => idx !== index),
                      }))
                    }
                    disabled={invoiceForm.lineItems.length <= 1}
                  >
                    Entfernen
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setInvoiceForm((prev) => ({
                    ...prev,
                    lineItems: [
                      ...prev.lineItems,
                      { assetId: '', description: '', quantity: 1, unitPrice: 0 },
                    ],
                  }))
                }
              >
                Position hinzufügen
              </button>
            </div>
            <button type="submit" className="primary">
              Rechnung speichern
            </button>
          </form>
          <table>
            <thead>
              <tr>
                <th>Nummer</th>
                <th>Kunde</th>
                <th>Datum</th>
                <th>Betrag</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{invoice.number}</td>
                  <td>{invoice.customerName}</td>
                  <td>{new Date(invoice.issueDate).toLocaleDateString('de-DE')}</td>
                  <td>{formatCurrency(invoice.totalAmount, invoice.currency)}</td>
                  <td className="table-actions">
                    <a href={invoiceDownloadUrl(invoice.id, 'xml')} className="link">
                      e-Rechnung (XML)
                    </a>
                    <a href={invoiceDownloadUrl(invoice.id, 'pdf')} className="link">
                      PDF
                    </a>
                    <button
                      type="button"
                      className="danger"
                      onClick={() =>
                        void withReload(
                          () => deleteInvoice(invoice.id),
                          'Rechnung gelöscht.'
                        )
                      }
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <form className="form" onSubmit={handleDeliverySubmit}>
            <h3>Lieferschein erstellen</h3>
            <div className="grid">
              <label>
                Nummer
                <input
                  value={deliveryForm.number}
                  onChange={(event) =>
                    setDeliveryForm({ ...deliveryForm, number: event.target.value })
                  }
                  required
                />
              </label>
              <label>
                Datum
                <input
                  type="date"
                  value={deliveryForm.issueDate}
                  onChange={(event) =>
                    setDeliveryForm({ ...deliveryForm, issueDate: event.target.value })
                  }
                  required
                />
              </label>
            </div>
            <label>
              Empfänger
              <input
                value={deliveryForm.recipientName}
                onChange={(event) =>
                  setDeliveryForm({
                    ...deliveryForm,
                    recipientName: event.target.value,
                  })
                }
                required
              />
            </label>
            <label>
              Adresse
              <textarea
                value={deliveryForm.recipientAddress}
                onChange={(event) =>
                  setDeliveryForm({
                    ...deliveryForm,
                    recipientAddress: event.target.value,
                  })
                }
              />
            </label>
            <div className="line-items">
              <h4>Positionen</h4>
              {deliveryForm.items.map((item, index) => (
                <div className="line-item" key={index}>
                  <select
                    value={item.assetId}
                    onChange={(event) =>
                      updateDeliveryItem(index, 'assetId', event.target.value)
                    }
                  >
                    <option value="">—</option>
                    {data.assets.map((asset) => (
                      <option key={asset.id} value={asset.id}>
                        {asset.name}
                      </option>
                    ))}
                  </select>
                  <input
                    placeholder="Beschreibung"
                    value={item.description}
                    onChange={(event) =>
                      updateDeliveryItem(index, 'description', event.target.value)
                    }
                  />
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(event) =>
                      updateDeliveryItem(index, 'quantity', Number(event.target.value))
                    }
                  />
                  <button
                    type="button"
                    className="danger"
                    onClick={() =>
                      setDeliveryForm((prev) => ({
                        ...prev,
                        items: prev.items.filter((_, idx) => idx !== index),
                      }))
                    }
                    disabled={deliveryForm.items.length <= 1}
                  >
                    Entfernen
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setDeliveryForm((prev) => ({
                    ...prev,
                    items: [...prev.items, { assetId: '', description: '', quantity: 1 }],
                  }))
                }
              >
                Position hinzufügen
              </button>
            </div>
            <button type="submit" className="primary">
              Lieferschein speichern
            </button>
          </form>
          <table>
            <thead>
              <tr>
                <th>Nummer</th>
                <th>Empfänger</th>
                <th>Datum</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {data.deliveryNotes.map((note) => (
                <tr key={note.id}>
                  <td>{note.number}</td>
                  <td>{note.recipientName}</td>
                  <td>{new Date(note.issueDate).toLocaleDateString('de-DE')}</td>
                  <td className="table-actions">
                    <a href={deliveryNoteDownloadUrl(note.id, 'xml')} className="link">
                      UBL XML
                    </a>
                    <a href={deliveryNoteDownloadUrl(note.id, 'pdf')} className="link">
                      PDF
                    </a>
                    <button
                      type="button"
                      className="danger"
                      onClick={() =>
                        void withReload(
                          () => deleteDeliveryNote(note.id),
                          'Lieferschein gelöscht.'
                        )
                      }
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    );
  }

  return (
    <div className="app">
      <nav>
        <h1>Warehouse Manager</h1>
        <ul>
          <li className={activeTab === 'dashboard' ? 'active' : ''}>
            <button type="button" onClick={() => setActiveTab('dashboard')}>
              Dashboard
            </button>
          </li>
          <li className={activeTab === 'types' ? 'active' : ''}>
            <button type="button" onClick={() => setActiveTab('types')}>
              Gerätetypen
            </button>
          </li>
          <li className={activeTab === 'locations' ? 'active' : ''}>
            <button type="button" onClick={() => setActiveTab('locations')}>
              Lagerorte
            </button>
          </li>
          <li className={activeTab === 'cases' ? 'active' : ''}>
            <button type="button" onClick={() => setActiveTab('cases')}>
              Cases
            </button>
          </li>
          <li className={activeTab === 'assets' ? 'active' : ''}>
            <button type="button" onClick={() => setActiveTab('assets')}>
              Geräte
            </button>
          </li>
          <li className={activeTab === 'peripherals' ? 'active' : ''}>
            <button type="button" onClick={() => setActiveTab('peripherals')}>
              Peripherie
            </button>
          </li>
          <li className={activeTab === 'circulation' ? 'active' : ''}>
            <button type="button" onClick={() => setActiveTab('circulation')}>
              Umläufe
            </button>
          </li>
          <li className={activeTab === 'documents' ? 'active' : ''}>
            <button type="button" onClick={() => setActiveTab('documents')}>
              Dokumente
            </button>
          </li>
        </ul>
      </nav>
      <main>
        {loading && <div className="info">Lade Daten…</div>}
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        {message && <div className="success">{message}</div>}
        {!loading && !data && !error && (
          <div className="error">Keine Daten verfügbar.</div>
        )}
        {!loading && data && (
          <div className="content">
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'types' && renderTypes()}
            {activeTab === 'locations' && renderLocations()}
            {activeTab === 'cases' && renderCases()}
            {activeTab === 'assets' && renderAssets()}
            {activeTab === 'peripherals' && renderPeripherals()}
            {activeTab === 'circulation' && renderCirculation()}
            {activeTab === 'documents' && renderDocuments()}
          </div>
        )}
      </main>
    </div>
  );
}
